import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { auth } from "../firebase";

// Reemplaza a Auth0Provider/useAuth0. El Backoffice autentica con cuentas
// Firebase Email/Password creadas a mano en la consola (una por admin),
// desacopladas de cualquier fila de Usuario que se use como jugador en la
// app — ver plan "Retirar Auth0, unificar 100% en Firebase" (2026-09-09).
// Default con la misma forma que el valor real (no null): igual que
// useAuth0(), useAuth() debe poder llamarse sin reventar si algo se renderiza
// sin <AuthProvider> alrededor (pasa, por ejemplo, en App.test.js).
const defaultAuthValue = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => Promise.reject(new Error("AuthProvider no está montado")),
  logout: () => Promise.resolve(),
  getIdToken: () => Promise.reject(new Error("AuthProvider no está montado"))
};

const AuthContext = createContext(defaultAuthValue);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, firebaseUser => {
      setUser(firebaseUser);
      setIsLoading(false);
    });
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  const getIdToken = () => {
    if (!auth.currentUser) {
      return Promise.reject(new Error("No hay sesión activa"));
    }
    return auth.currentUser.getIdToken();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        getIdToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Drop-in de @auth0/auth0-react's withAuthenticationRequired: mismo shape
// (Component, { onRedirecting }) usado en las vistas del Backoffice. No
// dispara login (pageRoutes.jsx ya gatea todo el app antes de montar
// cualquiera de estas rutas — no hay rama pública, ver pageRoutes.jsx) —
// solo cubre el mismo caso borde que
// cubría el original mientras isLoading/no autenticado.
export const withAuthenticationRequired = (Component, options = {}) => {
  const { onRedirecting = () => null } = options;
  return function WithAuthenticationRequired(props) {
    const { isAuthenticated, isLoading } = useAuth();
    if (isLoading || !isAuthenticated) {
      return onRedirecting();
    }
    return <Component {...props} />;
  };
};
